import { GameAudio } from './audio';
import { angleFromUp, directionFromAim, normalize, reflect, scale, subtract } from './math';
import { generatePatternSequence, patternToWorld } from './patterns';
import { createFlight, createScoreState, resetCombo, resolveRelayScore, simulateFlight, stepFlight } from './physics';
import { GameRenderer, type PreviewVisual, type RenderState } from './renderer';
import { loadSave, persistSave, updateRunSave, type SaveData } from './storage';
import type { FlightEvent, FlightState, PhysicsWorld, RelayPattern, ScoreState, Vec2 } from './types';
import { UIController, type StoredSettings } from '../ui';

type GameMode = 'title' | 'ready' | 'aiming' | 'flying' | 'transition' | 'paused' | 'gameover';
type ResumeMode = Exclude<GameMode, 'title' | 'aiming' | 'paused' | 'gameover'>;

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface RunStats {
  perfects: number;
  bankShots: number;
  bestCombo: number;
}

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

const FIXED_STEP = 1 / 120;
const FOCUS_LIMIT = 1.2;
const TRAIL_LIMIT = 38;

function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function easeOutCubic(value: number): number {
  const inverse = 1 - clamp(value);
  return 1 - inverse * inverse * inverse;
}

function haptic(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Vibration is optional and unavailable in iOS Safari.
  }
}

function todaySeed(): string {
  const date = new Date();
  return `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
}

export class LuxRelayGame {
  readonly renderer: GameRenderer;
  readonly audio: GameAudio;
  readonly ui: UIController;

  private readonly canvas: HTMLCanvasElement;
  private readonly motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  private save: SaveData;
  private mode: GameMode = 'title';
  private resumeMode: ResumeMode = 'ready';
  private patterns: RelayPattern[] = [];
  private pattern: RelayPattern | null = null;
  private world: PhysicsWorld | null = null;
  private patternIndex = 0;
  private seed = todaySeed();
  private flight: FlightState | null = null;
  private score: ScoreState = createScoreState();
  private runStats: RunStats = { perfects: 0, bankShots: 0, bestCombo: 0 };
  private drag: DragState | null = null;
  private aim = 0;
  private focusTime = 0;
  private pressure = 0.06;
  private transitionTime = 0;
  private gameOverDelay = 0;
  private gameOverShown = false;
  private failureCause: 'hazard' | 'darkness' | 'lost' = 'lost';
  private trail: Vec2[] = [];
  private accumulator = 0;
  private lastFrame = 0;
  private elapsed = 0;
  private wakeLock: WakeLockSentinelLike | null = null;
  private animationFrame = 0;
  private reducedMotion = false;
  private destroyed = false;

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement, liveStatus: HTMLElement) {
    this.canvas = canvas;
    this.save = loadSave(window.localStorage);
    this.renderer = new GameRenderer(canvas);
    this.audio = new GameAudio({
      initialMuted: this.save.settings.muted,
      storageKey: 'lux-relay-audio-muted-v1',
      onMutedChange: (muted) => {
        if (muted !== this.save.settings.muted) this.applySettings({ ...this.save.settings, muted });
      },
    });
    this.ui = new UIController(uiRoot, liveStatus, {
      onStart: () => this.startRun(false),
      onRetry: () => this.startRun(true),
      onPause: () => this.pause(false),
      onResume: () => this.resume(),
      onRestart: () => this.startRun(true),
      onHome: () => this.goHome(),
      onSettings: (settings) => this.applySettings(settings),
    }, this.save.settings);

    this.bindInput();
    this.applySettings(this.save.settings, false);
    this.ui.showTitle(this.save.bestScore);
    this.installDiagnostics();
    this.animationFrame = requestAnimationFrame((time) => this.frame(time));
  }

  startRun(retry: boolean): void {
    if (!retry || this.patterns.length === 0) {
      const requestedSeed = new URL(window.location.href).searchParams.get('seed');
      this.seed = requestedSeed ?? `${todaySeed()}-${this.save.runs}`;
      this.patterns = generatePatternSequence(this.seed, 256);
    }
    this.mode = 'ready';
    this.resumeMode = 'ready';
    this.patternIndex = 0;
    this.pattern = this.patterns[0] ?? null;
    this.world = this.pattern ? patternToWorld(this.pattern) : null;
    this.flight = null;
    this.score = createScoreState();
    this.runStats = { perfects: 0, bankShots: 0, bestCombo: 0 };
    this.drag = null;
    this.aim = 0;
    this.focusTime = 0;
    this.pressure = 0.06;
    this.transitionTime = 0;
    this.gameOverDelay = 0;
    this.gameOverShown = false;
    this.failureCause = 'lost';
    this.trail = [];
    this.accumulator = 0;
    this.renderer.clearEffects();
    this.audio.overdriveEnd(false);
    void this.audio.resume();
    this.ui.showPlaying();
    this.ui.updateHud(this.hudState());
    this.ui.showCoach('AUFLEGEN · NACH OBEN ZIEHEN', 'Loslassen feuert den Puls', 'KALIBRIERUNG', 0);
    this.ui.announce('Lauf gestartet. Finger auflegen, in Zielrichtung ziehen und loslassen.');
    void this.acquireWakeLock();
  }

  pause(fromSystem: boolean): void {
    if (this.mode === 'title' || this.mode === 'paused' || this.mode === 'gameover') return;
    this.cancelDrag(true);
    this.resumeMode = this.mode === 'aiming' ? 'ready' : this.mode;
    this.mode = 'paused';
    this.accumulator = 0;
    this.audio.pause();
    this.ui.showPaused(fromSystem);
    void this.releaseWakeLock();
  }

  resume(): void {
    if (this.mode !== 'paused') return;
    this.mode = this.resumeMode;
    this.lastFrame = performance.now();
    this.accumulator = 0;
    this.ui.showPlaying();
    void this.audio.resume();
    void this.acquireWakeLock();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    cancelAnimationFrame(this.animationFrame);
    this.audio.destroy();
    void this.releaseWakeLock();
  }

  private frame(timestamp: number): void {
    if (this.destroyed) return;
    const delta = this.lastFrame === 0 ? 0 : Math.min(0.05, Math.max(0, (timestamp - this.lastFrame) / 1000));
    this.lastFrame = timestamp;
    this.elapsed += delta * (this.mode === 'aiming' ? 0.32 : 1);
    this.update(delta);
    this.renderer.render(this.renderState(), delta);
    this.animationFrame = requestAnimationFrame((time) => this.frame(time));
  }

  private update(delta: number): void {
    if (this.mode === 'ready') {
      this.pressure += delta * 0.027;
    } else if (this.mode === 'aiming') {
      this.focusTime = Math.min(FOCUS_LIMIT, this.focusTime + delta);
      this.pressure += delta * 0.012;
      const intensity = this.focusTime / FOCUS_LIMIT;
      this.audio.focusUpdate(intensity);
      if (this.focusTime >= FOCUS_LIMIT) this.launch();
    } else if (this.mode === 'flying') {
      this.pressure += delta * 0.01;
      this.updateFlight(delta);
    } else if (this.mode === 'transition') {
      this.transitionTime += delta;
      if (this.transitionTime >= 0.46) this.loadNextPattern();
    } else if (this.mode === 'gameover' && !this.gameOverShown) {
      this.gameOverDelay -= delta;
      if (this.gameOverDelay <= 0) this.finalizeGameOver();
    }

    if ((this.mode === 'ready' || this.mode === 'aiming' || this.mode === 'flying') && this.pressure >= 1) {
      const position = this.flight?.position ?? this.pattern?.source.center ?? { x: 195, y: 730 };
      this.fail('darkness', position);
    }
    if (this.mode !== 'title') this.ui.updateHud(this.hudState());
  }

  private updateFlight(delta: number): void {
    if (!this.flight || !this.world) return;
    this.accumulator += delta;
    let steps = 0;
    while (this.accumulator >= FIXED_STEP && steps < 6 && this.mode === 'flying') {
      const activeWorld = this.score.overdriveRelays > 0 ? { ...this.world, hazards: [] } : this.world;
      const result = stepFlight(this.flight, activeWorld, FIXED_STEP);
      this.flight = result.state;
      this.accumulator -= FIXED_STEP;
      steps += 1;
      this.trail.push(this.flight.position);
      const trailLimit = this.reducedMotion ? 13 : TRAIL_LIMIT;
      if (this.trail.length > trailLimit) this.trail.splice(0, this.trail.length - trailLimit);
      this.handleFlightEvents(result.events);
    }
    if (steps >= 6) this.accumulator = 0;
  }

  private handleFlightEvents(events: readonly FlightEvent[]): void {
    for (const event of events) {
      if (event.type === 'mirror') {
        this.renderer.burst(event.point, this.score.overdriveRelays > 0 ? 'white' : 'amber', 12, 0.9);
        this.renderer.kick(0.42);
        this.audio.mirrorBounce(this.score.combo + 1, (event.point.x / 390) * 2 - 1);
        haptic(5);
      } else if (event.type === 'shard') {
        this.renderer.burst(event.point, 'cyan', 8, 0.55);
        this.audio.shard(this.flight?.collectedShardIds.length ?? 1, (event.point.x / 390) * 2 - 1);
      } else if (event.type === 'receiver' && this.mode === 'flying') {
        this.completeRelay(event);
      } else if (event.type === 'hazard') {
        this.renderer.burst(event.point, 'rose', 26, 1.15);
      } else if (event.type === 'failed' && this.mode === 'flying') {
        this.fail(event.reason === 'hazard' ? 'hazard' : 'lost', event.point);
      }
    }
  }

  private completeRelay(event: Extract<FlightEvent, { type: 'receiver' }>): void {
    if (!this.pattern || !this.flight || event.id !== this.pattern.target.id) return;
    const velocity = normalize(this.flight.velocity);
    const targetDelta = subtract(event.point, this.pattern.target.center);
    const hitOffset = Math.abs(targetDelta.x * velocity.y - targetDelta.y * velocity.x);
    const previousOverdrive = this.score.overdriveRelays;
    const resolution = resolveRelayScore(this.score, {
      successful: true,
      hitOffset,
      targetRadius: this.pattern.target.radius,
      bounces: this.flight.bounces,
      shards: this.flight.collectedShardIds.length,
      flightTime: this.flight.elapsed,
    });
    this.score = resolution.state;
    this.runStats.bestCombo = Math.max(this.runStats.bestCombo, this.score.combo);
    if (resolution.grade === 'center') this.runStats.perfects += 1;
    if (resolution.bankShot) this.runStats.bankShots += 1;
    this.pressure = Math.max(0.035, this.pressure - (resolution.overdriveTriggered ? 0.075 : 0.035));
    this.mode = 'transition';
    this.resumeMode = 'transition';
    this.transitionTime = 0;
    this.trail.push(this.pattern.target.center);

    const quality = resolution.grade === 'center' ? 'perfect' : resolution.grade === 'clean' ? 'clean' : 'safe';
    this.audio.receiverHit(quality, this.score.combo, (event.point.x / 390) * 2 - 1);
    this.renderer.burst(this.pattern.target.center, resolution.grade === 'center' ? 'white' : 'amber', resolution.grade === 'center' ? 34 : 22, 1.1);
    this.renderer.kick(resolution.grade === 'center' ? 0.9 : 0.55);
    this.renderer.flashScreen(resolution.grade === 'center' ? 'cyan' : 'amber', resolution.grade === 'center' ? 0.75 : 0.38);
    haptic(resolution.grade === 'center' ? [7, 20, 8] : 7);

    if (resolution.overdriveTriggered) {
      this.audio.overdriveStart();
      this.ui.showCoach('OVERDRIVE', 'Gefahrenglas wird für drei Relays durchlässig', `+${resolution.points} · FLOW ×${this.score.combo}`, 1150);
    } else {
      if (previousOverdrive > 0 && this.score.overdriveRelays === 0) this.audio.overdriveEnd();
      const label = resolution.grade === 'center' ? 'PERFEKT' : resolution.grade === 'clean' ? 'SAUBER' : 'RELAY';
      const extras = [resolution.bankShot ? `${this.flight.bounces} BANDE${this.flight.bounces > 1 ? 'N' : ''}` : '', this.flight.collectedShardIds.length ? `${this.flight.collectedShardIds.length} SPLITTER` : ''].filter(Boolean).join(' · ');
      this.ui.showCoach(`${label} · +${resolution.points}`, extras || `FLOW ×${this.score.combo}`, this.score.combo > 1 ? `FLOW ×${this.score.combo}` : 'SIGNAL STABIL', 760);
    }
    this.ui.announce(`${resolution.grade === 'center' ? 'Perfektes' : 'Erfolgreiches'} Relay. ${resolution.points} Punkte.`);
  }

  private loadNextPattern(): void {
    this.patternIndex += 1;
    this.pattern = this.patterns[this.patternIndex] ?? null;
    if (!this.pattern) {
      this.patterns.push(...generatePatternSequence(`${this.seed}-${this.patternIndex}`, 128));
      this.pattern = this.patterns[this.patternIndex] ?? this.patterns[0] ?? null;
    }
    this.world = this.pattern ? patternToWorld(this.pattern) : null;
    this.flight = null;
    this.trail = [];
    this.drag = null;
    this.aim = 0;
    this.focusTime = 0;
    this.accumulator = 0;
    this.mode = 'ready';
    this.resumeMode = 'ready';
    if (this.patternIndex === 1) this.ui.showCoach('ZIEH IN ZIELRICHTUNG', 'Die gestrichelte Linie ist dein Winkel', 'SIGNAL 02', 2200);
    if (this.patternIndex === 3) this.ui.showCoach('GLAS LENKT LICHT', 'Nutze die leuchtende Kante als Bande', 'NEUE REGEL', 3000);
    if (this.patternIndex === 4) this.ui.showCoach('JETZT ZÄHLT FLOW', 'Center-Treffer laden den Overdrive', 'SYSTEM FREI', 2200);
  }

  private fail(cause: 'hazard' | 'darkness' | 'lost', position: Vec2): void {
    if (this.mode === 'gameover' || this.mode === 'title') return;
    this.cancelDrag(true);
    this.failureCause = cause;
    this.mode = 'gameover';
    this.gameOverDelay = this.reducedMotion ? 0.12 : 0.34;
    this.gameOverShown = false;
    this.score = resetCombo(this.score);
    this.audio.failure();
    this.audio.overdriveEnd(false);
    this.renderer.burst(position, 'rose', 38, 1.35);
    this.renderer.burst(position, 'glass', 34, 1.2);
    this.renderer.kick(1.7);
    this.renderer.flashScreen('rose', 0.72);
    haptic([12, 24, 18]);
    void this.releaseWakeLock();
  }

  private finalizeGameOver(): void {
    if (this.gameOverShown) return;
    this.gameOverShown = true;
    const oldBest = this.save.bestScore;
    const relays = this.patternIndex;
    this.save = updateRunSave(this.save, {
      score: this.score.total,
      relays,
      perfects: this.runStats.perfects,
      bankShots: this.runStats.bankShots,
      bestCombo: this.runStats.bestCombo,
    });
    persistSave(this.save, window.localStorage);
    this.ui.showGameOver({
      score: this.score.total,
      best: this.save.bestScore,
      relay: relays,
      perfects: this.runStats.perfects,
      bankShots: this.runStats.bankShots,
      newBest: this.score.total > oldBest,
      cause: this.failureCause,
    });
  }

  private beginAim(event: PointerEvent): void {
    if (this.mode !== 'ready' || !event.isPrimary) return;
    event.preventDefault();
    try { this.canvas.setPointerCapture(event.pointerId); } catch { /* Synthetic and already-cancelled pointers cannot be captured. */ }
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
    };
    this.mode = 'aiming';
    this.resumeMode = 'ready';
    this.aim = 0;
    this.focusTime = 0;
    this.audio.focusStart(0.1);
    this.ui.hideCoach();
  }

  private updateAim(event: PointerEvent): void {
    if (this.mode !== 'aiming' || !this.drag || event.pointerId !== this.drag.pointerId) return;
    event.preventDefault();
    this.drag.currentX = event.clientX;
    this.drag.currentY = event.clientY;
    const delta = { x: this.drag.currentX - this.drag.startX, y: this.drag.currentY - this.drag.startY };
    if (Math.hypot(delta.x, delta.y) >= 10) this.aim = angleFromUp(delta);
    this.aim = clamp(this.aim, -Math.PI * 0.4, Math.PI * 0.4);
  }

  private launch(): void {
    if ((this.mode !== 'aiming' && this.mode !== 'ready') || !this.pattern) return;
    this.cancelDrag(false);
    this.flight = createFlight(this.pattern.source, this.aim);
    this.trail = [this.pattern.source.center];
    this.accumulator = 0;
    this.mode = 'flying';
    this.resumeMode = 'flying';
    this.audio.focusEnd(false);
    this.audio.launch(0.8 + this.focusTime / FOCUS_LIMIT * 0.2, Math.sin(this.aim));
    this.renderer.burst(this.pattern.source.center, 'cyan', 16, 0.72);
    this.renderer.kick(0.25);
    haptic(4);
  }

  private cancelDrag(cancelled: boolean): void {
    const pointerId = this.drag?.pointerId;
    this.drag = null;
    this.focusTime = cancelled ? 0 : this.focusTime;
    if (pointerId !== undefined && this.canvas.hasPointerCapture(pointerId)) {
      try { this.canvas.releasePointerCapture(pointerId); } catch { /* Pointer already released. */ }
    }
    if (cancelled) {
      this.audio.focusEnd(true);
      if (this.mode === 'aiming') this.mode = 'ready';
    }
  }

  private preview(): PreviewVisual | null {
    if (this.mode !== 'aiming' || !this.pattern || !this.world) return null;
    const activeWorld = this.score.overdriveRelays > 0 ? { ...this.world, hazards: [] } : this.world;
    const result = simulateFlight(activeWorld, this.pattern.source.id, this.aim);
    const firstMirror = result.events.find((event): event is Extract<FlightEvent, { type: 'mirror' }> => event.type === 'mirror');
    const direction = directionFromAim(this.aim);
    if (!firstMirror) {
      return {
        points: [this.pattern.source.center, {
          x: this.pattern.source.center.x + direction.x * 235,
          y: this.pattern.source.center.y + direction.y * 235,
        }],
        valid: true,
        bounces: 0,
      };
    }
    const reflected = reflect(direction, firstMirror.normal);
    return {
      points: [this.pattern.source.center, firstMirror.point, {
        x: firstMirror.point.x + reflected.x * 145,
        y: firstMirror.point.y + reflected.y * 145,
      }],
      valid: true,
      bounces: 1,
    };
  }

  private renderState(): RenderState {
    const pattern = this.pattern;
    const pulsePosition = this.mode === 'transition' && pattern
      ? pattern.target.center
      : this.flight?.position ?? pattern?.source.center ?? null;
    const visualMode: RenderState['mode'] = this.mode === 'title'
      ? 'title'
      : this.mode === 'paused'
        ? 'paused'
        : this.mode === 'gameover'
          ? 'gameover'
          : this.mode;
    const transitionProgress = this.mode === 'transition' ? easeOutCubic(this.transitionTime / 0.46) : 0;
    return {
      mode: visualMode,
      time: this.elapsed,
      current: pattern ? { center: pattern.source.center, radius: pattern.source.radius, current: true, pulse: this.mode === 'aiming' ? this.focusTime / FOCUS_LIMIT : 0 } : null,
      target: pattern ? { center: pattern.target.center, radius: pattern.target.radius, pulse: Math.sin(this.elapsed * 2.4) * 0.5 + 0.5 } : null,
      mirrors: pattern?.mirrors.map((mirror) => ({ a: mirror.a, b: mirror.b, required: pattern.requiresBank })) ?? [],
      hazards: pattern?.hazards.map((hazard) => hazard.kind === 'circle'
        ? { kind: 'circle' as const, center: hazard.center, radius: hazard.radius }
        : { kind: 'segment' as const, a: hazard.a, b: hazard.b }) ?? [],
      shards: pattern?.shards.map((shard) => ({
        center: shard.center,
        radius: shard.radius,
        collected: this.flight?.collectedShardIds.includes(shard.id) ?? false,
      })) ?? [],
      pulse: pulsePosition ? {
        position: pulsePosition,
        velocity: this.flight?.velocity ?? scale(directionFromAim(this.aim), 1),
        radius: 5.5,
        moving: this.mode === 'flying',
        trail: this.trail,
      } : null,
      preview: this.preview(),
      pressure: clamp(this.pressure),
      focus: this.mode === 'aiming' ? this.focusTime / FOCUS_LIMIT : 0,
      overdrive: this.score.overdriveRelays / 3,
      relay: this.patternIndex + 1,
      roomOffset: transitionProgress * 34,
    };
  }

  private hudState() {
    return {
      score: this.score.total,
      combo: this.score.combo,
      relay: this.patternIndex + 1,
      focus: this.mode === 'aiming' ? this.focusTime / FOCUS_LIMIT : 0,
      pressure: 1 - this.pressure,
      overdrive: this.score.overdriveRelays / 3,
    };
  }

  private applySettings(settings: StoredSettings, persist = true): void {
    this.save = { ...this.save, settings: { ...settings } };
    if (persist) persistSave(this.save, window.localStorage);
    this.audio.setMuted(settings.muted, false);
    this.reducedMotion = settings.motion === 'reduced' || (settings.motion === 'auto' && this.motionMedia.matches);
    this.renderer.setReducedMotion(this.reducedMotion);
    document.documentElement.dataset.reducedMotion = String(this.reducedMotion);
  }

  private goHome(): void {
    this.cancelDrag(true);
    this.mode = 'title';
    this.pattern = null;
    this.world = null;
    this.flight = null;
    this.trail = [];
    this.audio.overdriveEnd(false);
    this.ui.showTitle(this.save.bestScore);
    void this.releaseWakeLock();
  }

  private bindInput(): void {
    this.canvas.addEventListener('pointerdown', (event) => this.beginAim(event));
    this.canvas.addEventListener('pointermove', (event) => this.updateAim(event));
    this.canvas.addEventListener('pointerup', (event) => {
      if (this.drag?.pointerId !== event.pointerId) return;
      event.preventDefault();
      this.launch();
    });
    this.canvas.addEventListener('pointercancel', (event) => {
      if (this.drag?.pointerId === event.pointerId) this.cancelDrag(true);
    });
    this.canvas.addEventListener('lostpointercapture', (event) => {
      if (this.drag?.pointerId === event.pointerId && this.mode === 'aiming') this.cancelDrag(true);
    });

    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (this.mode === 'paused') this.resume();
        else this.pause(false);
        return;
      }
      if (this.mode === 'title' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.startRun(false);
        return;
      }
      if (this.mode === 'gameover' && this.gameOverShown && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.startRun(true);
        return;
      }
      if (this.mode === 'ready' || this.mode === 'aiming') {
        if (event.key === 'ArrowLeft' || event.key.toLowerCase() === 'a') {
          event.preventDefault();
          this.mode = 'aiming';
          this.aim = clamp(this.aim - 0.045, -Math.PI * .4, Math.PI * .4);
        }
        if (event.key === 'ArrowRight' || event.key.toLowerCase() === 'd') {
          event.preventDefault();
          this.mode = 'aiming';
          this.aim = clamp(this.aim + 0.045, -Math.PI * .4, Math.PI * .4);
        }
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.launch();
        }
      }
    });

    this.motionMedia.addEventListener('change', () => {
      if (this.save.settings.motion === 'auto') this.applySettings(this.save.settings, false);
    });
    document.addEventListener('visibilitychange', () => {
      this.audio.handleVisibilityChange(document.hidden);
      if (document.hidden) this.pause(true);
      this.lastFrame = performance.now();
    });
    window.addEventListener('pagehide', () => {
      this.audio.handleVisibilityChange(true);
      this.pause(true);
    });
    window.addEventListener('pageshow', () => { this.lastFrame = performance.now(); });
    window.addEventListener('blur', () => {
      if (this.mode === 'aiming') this.cancelDrag(true);
    });
  }

  private async acquireWakeLock(): Promise<void> {
    const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } }).wakeLock;
    if (!wakeLock || this.wakeLock) return;
    try {
      this.wakeLock = await wakeLock.request('screen');
    } catch {
      this.wakeLock = null;
    }
  }

  private async releaseWakeLock(): Promise<void> {
    const lock = this.wakeLock;
    this.wakeLock = null;
    try { await lock?.release(); } catch { /* The OS may already have released it. */ }
  }

  private installDiagnostics(): void {
    window.__LUX_RELAY_DIAGNOSTICS__ = {
      getState: () => ({
        mode: this.mode,
        score: this.score.total,
        combo: this.score.combo,
        relay: this.patternIndex + 1,
        pressure: this.pressure,
        solutionAim: this.pattern?.solutionAim ?? 0,
        pattern: this.pattern?.family ?? null,
        muted: this.audio.muted,
        reducedMotion: this.reducedMotion,
        best: this.save.bestScore,
      }),
      getPerformance: () => this.renderer.getMetrics(),
    };
    if (new URL(window.location.href).searchParams.get('test') === '1') {
      window.__LUX_RELAY_TEST__ = {
        start: () => this.startRun(false),
        retry: () => this.startRun(true),
        shootSolution: () => {
          if (this.mode !== 'ready' || !this.pattern) return;
          this.aim = this.pattern.solutionAim;
          this.launch();
        },
        fail: (cause = 'lost') => this.fail(cause, this.flight?.position ?? this.pattern?.source.center ?? { x: 195, y: 730 }),
      };
    }
  }
}
